import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, Clock, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { api } from "@/lib/api";

const when = (value) => value ? new Date(value).toLocaleString() : "—";

export default function AdminDeveloperMonitor() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const response = await api.get("/developer-monitor/admin/status");
            setData(response.data);
        } catch {
            toast.error("Could not load developer monitoring data.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = window.setInterval(load, 5_000);
        return () => window.clearInterval(timer);
    }, [load]);

    const decide = async (reportId, recommendationId, decision) => {
        try {
            await api.post(`/developer-monitor/admin/reports/${reportId}/decision`, {
                recommendation_id: recommendationId,
                decision,
                note: decision === "approved" ? "Approved for developer planning; no automatic code change." : "Rejected by admin.",
            });
            toast.success(`Recommendation ${decision}.`);
            load();
        } catch {
            toast.error("Could not save the decision.");
        }
    };

    const active = data?.active || [];
    const reports = data?.reports || [];
    const events = data?.events || [];

    return (
        <div className="ww-fade-up" data-testid="developer-monitor-page">
            <PageHeader title="Developer Monitor" subtitle="Live browser errors, API traffic, health reports and approval-gated recommendations.">
                <button onClick={load} className="h-9 px-3 border border-border text-sm text-white hover:border-volt flex items-center gap-2">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </PageHeader>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-surface border border-border p-5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Monitor</div>
                    <div className="mt-2 flex items-center gap-2 text-xl font-heading font-bold">
                        <span className={`w-2.5 h-2.5 rounded-full ${active.length ? "bg-success animate-pulse" : "bg-muted-foreground"}`} />
                        {active.length ? "Live" : "Standby"}
                    </div>
                </div>
                <div className="bg-surface border border-border p-5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Active sessions</div>
                    <div className="mt-2 text-2xl font-mono text-white">{active.length}</div>
                </div>
                <div className="bg-surface border border-border p-5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Pending approvals</div>
                    <div className="mt-2 text-2xl font-mono text-warning">
                        {reports.flatMap((report) => report.recommendations || []).filter((item) => item.status === "pending").length}
                    </div>
                </div>
            </div>

            <section className="bg-surface border border-border p-5 mb-6">
                <h2 className="font-heading font-bold flex items-center gap-2 mb-4"><Activity className="w-4 h-4 text-success" /> Active monitoring</h2>
                {!active.length ? <p className="text-sm text-muted-foreground">Monitoring starts automatically when the configured developer or any staff account is signed in.</p> : (
                    <div className="space-y-3">
                        {active.map((session) => (
                            <div key={session.session_id} className="border border-border/70 bg-obsidian p-3 flex flex-wrap justify-between gap-3 text-sm">
                                <div><div className="text-white">{session.email}</div><div className="text-xs text-muted-foreground">{session.role} · {session.path}</div></div>
                                <div className="text-xs text-muted-foreground"><Clock className="w-3.5 h-3.5 inline mr-1" />Last seen {when(session.last_seen)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="bg-surface border border-border p-5 mb-6">
                <h2 className="font-heading font-bold flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-warning" /> Recent events</h2>
                <div className="overflow-x-auto max-h-[360px]">
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-muted-foreground"><tr><th className="text-left py-2">Time</th><th className="text-left">Source</th><th className="text-left">Event</th><th className="text-right">Status</th><th className="text-right">Latency</th></tr></thead>
                        <tbody>
                            {events.map((event) => (
                                <tr key={event.id} className="border-t border-border/60">
                                    <td className="py-2 pr-3 whitespace-nowrap text-xs text-muted-foreground">{when(event.created_at)}</td>
                                    <td className="pr-3 capitalize">{event.source}</td>
                                    <td className={event.level === "error" ? "text-alert" : event.level === "warning" ? "text-warning" : "text-white"}>{event.message}</td>
                                    <td className="text-right font-mono">{event.status || "—"}</td>
                                    <td className="text-right font-mono">{event.duration_ms != null ? `${Math.round(event.duration_ms)}ms` : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!events.length && <p className="text-sm text-muted-foreground py-3">No telemetry recorded yet.</p>}
                </div>
            </section>

            <section className="space-y-4">
                <h2 className="font-heading font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-volt" /> Completed reports</h2>
                {!reports.length && <div className="bg-surface border border-border p-5 text-sm text-muted-foreground">A report appears after monitoring stops or its heartbeat times out.</div>}
                {reports.map((report) => (
                    <div key={report.id} className="bg-surface border border-border p-5">
                        <div className="flex flex-wrap justify-between gap-3 mb-4">
                            <div><div className="text-white font-semibold">{report.email}</div><div className="text-xs text-muted-foreground">Stopped {when(report.stopped_at)} · {report.stop_reason}</div></div>
                            <span className={`text-xs uppercase tracking-wider px-2 py-1 border ${report.health === "healthy" ? "text-success border-success/40" : "text-warning border-warning/40"}`}>{report.health}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-4">
                            <div>Requests <span className="font-mono text-white">{report.summary?.requests || 0}</span></div>
                            <div>Errors <span className="font-mono text-alert">{report.summary?.errors || 0}</span></div>
                            <div>Warnings <span className="font-mono text-warning">{report.summary?.warnings || 0}</span></div>
                            <div>Max latency <span className="font-mono text-white">{Math.round(report.summary?.max_latency_ms || 0)}ms</span></div>
                        </div>
                        {(report.recommendations || []).map((item) => (
                            <div key={item.id} className="border-t border-border/60 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                                <div><div className="text-sm text-white"><span className="font-mono text-warning mr-2">{item.priority}</span>{item.title}</div><p className="text-xs text-muted-foreground mt-1">{item.rationale}</p></div>
                                {item.status === "pending" ? (
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={() => decide(report.id, item.id, "approved")} className="h-8 px-3 border border-success/50 text-success text-xs flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                                        <button onClick={() => decide(report.id, item.id, "rejected")} className="h-8 px-3 border border-alert/50 text-alert text-xs flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Reject</button>
                                    </div>
                                ) : <span className="text-xs uppercase text-muted-foreground">{item.status}</span>}
                            </div>
                        ))}
                        {!report.recommendations?.length && <p className="text-sm text-success">No blockers or enhancement recommendations were generated.</p>}
                    </div>
                ))}
            </section>
        </div>
    );
}
